import noop from "@/services/noop";
import { t } from "@lingui/macro";
import { Box, Button, Container, IconButton, Typography } from "@mui/material";
import { AddIcon, EditIcon, MenuList, BottomSheet } from "@WahooFitness/redesignr";
import { useEffect, useMemo } from "react";
import useAlertList from "./useAlertList";
import {
  bottomSafeArea,
  ErrorBoundary,
  GlobalHeader,
  useConfigContext,
} from "@WahooFitness/wahoo-offline-mfe";

export const EditButton = ({
  editing,
  toggleEditing,
  disabled = false,
}: {
  editing: boolean;
  toggleEditing: () => void;
  disabled?: boolean;
}) => (
  <Box mr={1}>
    {editing ? (
      <Button
        size="small"
        variant="text"
        color="info"
        onClick={toggleEditing}
        disabled={disabled}
        data-testid="editAlertsButton"
      >
        <Typography variant="prose-base">{t`Done`}</Typography>
      </Button>
    ) : (
      <IconButton onClick={toggleEditing} disabled={disabled} data-testid="doneEditingButton">
        <EditIcon />
      </IconButton>
    )}
  </Box>
);

const AlertList = ({
  workoutProfileId,
  editing: editingOverride,
}: {
  workoutProfileId: string;
  hasOwnHeader?: boolean;
  editing?: boolean;
}) => {
  const {
    editing,
    toggleEditing,
    alertsLoading,
    alertsError,
    currentEditorView,
    editorOpen,
    clearEditor,
    generalListTitle,
    generalListItems,
    navigationAlertListTitle,
    navigationAlertListItems,
    plannedWorkoutAlertListTitle,
    plannedWorkoutAlertListItems,
    customAlertListTitle,
    customAlertListItems,
    handleAddAlert,
    alertUpdateInProgress,
  } = useAlertList(workoutProfileId);

  useEffect(() => {
    if (editingOverride !== undefined && editingOverride !== editing) {
      toggleEditing();
    }
  }, [editing, editingOverride, toggleEditing]);

  const { globalBottomPadding } = useConfigContext();

  const editButton = useMemo(() => {
    if (customAlertListItems.length > 0) {
      return (
        <EditButton
          editing={editing}
          toggleEditing={toggleEditing}
          disabled={alertUpdateInProgress}
        />
      );
    }
    return undefined;
  }, [alertUpdateInProgress, customAlertListItems.length, editing, toggleEditing]);

  return (
    <>
      <GlobalHeader
        title={t`Alerts`}
        primaryRightActionItem={{
          componentOverride: editButton,
        }}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary isLoading={alertsLoading} error={alertsError}>
          <Box maxWidth="sm" width="100%" height="100%" overflow="scroll">
            <Box px={2} py={1}>
              <Box px={2}>{generalListTitle}</Box>
              <MenuList listItems={generalListItems} disableGutters />
            </Box>
            {navigationAlertListItems.length > 0 && (
              <Box px={2} py={1}>
                <Box px={2}>{navigationAlertListTitle}</Box>
                <MenuList listItems={navigationAlertListItems} disableGutters />
              </Box>
            )}
            {plannedWorkoutAlertListItems.length > 0 && (
              <Box px={2} py={1}>
                <Box px={2}>{plannedWorkoutAlertListTitle}</Box>
                <MenuList listItems={plannedWorkoutAlertListItems} disableGutters />
              </Box>
            )}
            {customAlertListItems.length > 0 && (
              <Box>
                <Box px={2} py={1}>
                  <Box px={2}>{customAlertListTitle}</Box>
                  <MenuList listItems={customAlertListItems} disableGutters />
                </Box>
              </Box>
            )}
            <Box width="100%" p={2}>
              <Button
                size="large"
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleAddAlert}
                data-testid="addAlertButton"
              >{t`Add a custom alert`}</Button>
            </Box>
          </Box>
          <BottomSheet
            open={editorOpen}
            onClose={clearEditor}
            onOpen={noop}
            PaperProps={{ sx: { height: "auto", maxHeight: "75%" } }}
            bottomPadding={bottomSafeArea(globalBottomPadding)}
            sx={{ zIndex: 20000 }}
          >
            {currentEditorView}
          </BottomSheet>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default AlertList;
